import type {
  ColumnHelper,
  PaginationState,
  Row,
  SortingState,
} from '@tanstack/react-table';
import { createColumnHelper, functionalUpdate } from '@tanstack/react-table';
import type { WritableAtom } from 'jotai';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { paginationAtom as defaultPaginationAtom } from '#pie/stores/pagination';
import type { Optional } from '#pie/types';
import { ErrorMessageBlock } from '../error-message/ErrorMessageBlock';
import type { TableProps } from '../table/Table';
import { Table } from '../table/Table';
import { Text } from '../text/Text';

interface Props<TData extends object>
  extends Optional<
    Omit<
      TableProps<TData>,
      | 'columns'
      | 'emphasizedRows'
      | 'pageCount'
      | 'pagination'
      | 'sorting'
      | 'onPaginationChange'
      | 'onSortingChange'
    >,
    'data'
  > {
  sortingAtom: WritableAtom<SortingState, [SortingState], SortingState>;
  columns: (helper: ColumnHelper<TData>) => TableProps<TData>['columns'];
  paginationAtom?: WritableAtom<
    PaginationState,
    [PaginationState],
    PaginationState
  >;
  totalCount?: number;
  isLoading?: boolean | number;
  error?: unknown;
}

export const DataTable = <TData extends object>({
  totalCount,
  columns,
  data,
  sortingAtom,
  paginationAtom = defaultPaginationAtom,
  getRowId: getRowIdProp,
  error,
  ...props
}: Props<TData>) => {
  const { t } = useTranslation();
  const helper = useMemo(() => createColumnHelper<TData>(), []);

  const [pagination, setPagination] = useAtom(paginationAtom);
  const [sorting, setSorting] = useAtom(sortingAtom);

  const pageCount = totalCount
    ? Math.ceil(totalCount / pagination.pageSize)
    : -1;

  const unreadIds = hasUnreadProp(data)
    ? data.filter((row) => row.hasUnreadComments).map(({ id }) => id)
    : [];

  // NOTE: Ignoring as the rowId is not easily targetable in tests
  /* istanbul ignore next -- @preserve */
  const getRowId = (
    originalRow: TData,
    index: number,
    parent?: Row<TData> | undefined,
  ): string => {
    let id = getRowIdProp?.(originalRow, index, parent);

    if (!id && 'id' in originalRow && typeof originalRow.id === 'string') {
      id = originalRow.id;
    }

    if (!id) {
      throw new Error('Table rows must have an id');
    }
    return id;
  };

  if (error) {
    return <ErrorMessageBlock error={error} />;
  }

  return !props.isLoading && !totalCount ? (
    <Text className="p-3">{t('common.no_results')}</Text>
  ) : (
    <Table
      /* istanbul ignore next -- @preserve */
      columns={columns(helper).map((column) =>
        column.id
          ? column
          : hasAccessorKey(column)
            ? { ...column, id: column.accessorKey }
            : column,
      )}
      sortDescFirst
      manualSorting
      data={data}
      emphasizedRows={unreadIds}
      enableMultiSort={false}
      getFilteredRowModel={undefined}
      getSortedRowModel={undefined}
      onPaginationChange={(paginationUpdater) => {
        const newPaginationVal = functionalUpdate(
          paginationUpdater,
          pagination,
        );
        setPagination(newPaginationVal);
      }}
      onSortingChange={(sortingUpdater) => {
        const newSortVal = functionalUpdate(sortingUpdater, sorting);
        setSorting(newSortVal);
      }}
      pageCount={pageCount}
      pageSizes={[10, 25, 50, 100]}
      pagination={pagination}
      sorting={sorting}
      totalCount={totalCount}
      getRowId={getRowId}
      {...props}
    />
  );
};

const hasUnreadProp = <T,>(
  data?: T[],
): data is (T & { id: string; hasUnreadComments: boolean })[] =>
  !!data &&
  data.some(
    (row) =>
      typeof row === 'object' &&
      row !== null &&
      Object.hasOwn(row, 'hasUnreadComments') &&
      Object.hasOwn(row, 'id'),
  );

const hasAccessorKey = (column: object): column is { accessorKey: string } =>
  !!column &&
  typeof column === 'object' &&
  column !== null &&
  Object.hasOwn(column, 'accessorKey');
